/**
 * CEOFleetSummary — big-picture Fleet card for the CEO Executive Command
 * Center. No granular vehicle detail. Just Total · Active Assignments ·
 * Pending Maintenance (by field/admin).
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Truck, AlertTriangle, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CEOFleetSummary() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/procurement/vehicles/fleet-summary`, { credentials: 'include' });
        if (r.ok) setSummary(await r.json());
      } catch (_) { /* noop */ }
    })();
  }, []);

  if (!summary) return null;
  const pending = summary.pending_maintenance || { total: 0, field: 0, admin: 0 };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="ceo-fleet-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5" style={{ color: '#C9A962' }} />
          <p className="text-xs uppercase tracking-widest" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
            Fleet Overview
          </p>
        </div>
        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">Big-picture only</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SummaryStat
          icon={<Truck className="h-4 w-4" />}
          label="Total Vehicles"
          value={summary.total_vehicles}
          sub={`${summary.field_total} field · ${summary.admin_total} admin`}
          testid="fleet-total"
        />
        <SummaryStat
          icon={<Users className="h-4 w-4" />}
          label="Active Assignments"
          value={summary.active_assignments}
          sub="Assigned to staff"
          testid="fleet-assigned"
        />
        <SummaryStat
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Pending Maintenance"
          value={pending.total}
          sub={`${pending.field} field · ${pending.admin} admin`}
          accent={pending.total > 0 ? '#f87171' : '#ffffff'}
          testid="fleet-pending"
        />
      </div>
      <p className="text-[10px] text-zinc-500 mt-3">Detail is managed by each department's Director. CEO board shows totals only.</p>
    </Card>
  );
}

function SummaryStat({ icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-lg p-3 border border-zinc-800 bg-zinc-950/60" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-zinc-400">
        {icon}
        <p className="text-[10px] uppercase tracking-widest">{label}</p>
      </div>
      <p className="text-2xl font-bold" style={{ color: accent || '#ffffff', fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
      <p className="text-[11px] text-zinc-500">{sub}</p>
    </div>
  );
}
